import { ConvertRGBAToHex } from "app/app.const";
import { Company, MediaObject, Paging, Product, Project, RawJsonDict, Site, Tag, TheApp, User } from "./base.model";

export class EnrichedEpcs extends RawJsonDict {
    attributes: any;
    epc: string;
    epcClass: string;
    epcName: string;
    type: string;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): EnrichedEpcs | null {
        if (!data) {
            return null;
        }

        const instance = new EnrichedEpcs(data);
        instance.attributes = data.attributes;
        instance.epc = data.epc;
        instance.epcClass = data.epcClass ? data.epcClass : data.epc;
        instance.epcName = data.epcName ? data.epcName : data.epc;
        instance.type = data.type;

        return instance;
    }
}

export class EnrichedInputOutputEpcs extends RawJsonDict {
    attributes: any;
    epcClass: string;
    epcName: string;
    humanReadable: string;
    quantity: number;
    type: string;
    unitOfMeasure: string;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): EnrichedInputOutputEpcs | null {
        if (!data) {
            return null;
        }

        const instance = new EnrichedInputOutputEpcs(data);
        instance.attributes = data.attributes;
        instance.epcClass = data.epcClass;
        instance.epcName = data.epcName;
        instance.humanReadable = data.humanReadable;
        instance.quantity = data.quantity;
        instance.type = data.type;
        instance.unitOfMeasure = data.unitOfMeasure;

        return instance;
    }
}

export interface EpcisEventItem {
    displayName: string;
    value: string;
    type?: string;
    typeDisplayName?: string;
}

export class EpcisEvent extends RawJsonDict {
    documentId: string;
    sagaEventId: string;
    action: string;
    eventType: string;
    parentId?: string;
    bizLocation: EpcisEventItem;
    bizStep: EpcisEventItem;
    bizTransactions: EpcisEventItem[] = [];
    customTags: any;
    destinations: EpcisEventItem[] = [];
    disposition: EpcisEventItem;
    readPoint: EpcisEventItem;
    sources: EpcisEventItem[] = [];
    epcs: string[] = [];
    inputEpcs: any[] = [];
    inputQuantities: any[] = [];
    instanceLotMasterData: any;
    partition?: number;
    quantities: any[] = [];
    eventTime: string;
    eventDate: Date;
    eventTimeZoneOffset: string;
    recordTime: string;
    recordDate: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): EpcisEvent | null {
        if (!data) {
            return null;
        }

        const obj = new EpcisEvent(data);
        obj.documentId = data.documentId;
        obj.sagaEventId = data.sagaEventId;
        obj.action = data.action;
        obj.eventType = data.eventType;
        obj.parentId = data.parentId;

        obj.bizLocation = data.bizLocation ?? {};
        if (!obj.bizLocation.type) {
            obj.bizLocation.type = 'urn:epcglobal:epcis:vtype:BusinessLocation';
        }

        obj.bizStep = data.bizStep ?? {};
        obj.bizTransactions = data.bizTransactions ?? [];
        obj.customTags = data.customTags;
        obj.destinations = data.destinations ?? [];
        obj.disposition = data.disposition ?? {};

        obj.readPoint = data.readPoint ?? {};
        if (!obj.readPoint.type) {
            obj.readPoint.type = 'urn:epcglobal:epcis:vtype:ReadPoint';
        }

        obj.sources = data.sources ?? [];
        obj.epcs = data.epcs ?? [];
        obj.inputEpcs = data.inputEpcs ?? [];
        obj.inputQuantities = data.inputQuantities ?? [];
        obj.instanceLotMasterData = data.instanceLotMasterData;
        obj.partition = data.partition;
        obj.quantities = data.quantities ?? [];
        obj.eventTime = data.eventTime;
        obj.eventTimeZoneOffset = data.eventTimeZoneOffset;
        obj.recordTime = data.recordTime;
        obj.eventDate = new Date(data.eventTime);
        obj.recordDate = new Date(data.recordTime);

        return obj;
    }

    getEventTypeDisplay(): string {
        return this.eventType.replace('Event', '');
    }

}

export class Epcis extends RawJsonDict {
    enrichedEpcs: EnrichedEpcs[] = [];
    enrichedInputEpcs: EnrichedEpcs[] = [];
    enrichedInputQuantities: EnrichedInputOutputEpcs[] = [];
    enrichedQuantities: EnrichedInputOutputEpcs[] = [];
    enrichedParentId?: EnrichedEpcs | null;
    event: EpcisEvent;
    extension: any[] = [];
    listExtensions: any[] = [];
    compliance?: Compliance;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): Epcis | null {
        if (!data) {
            return null;
        }

        const obj = new Epcis(data);
        obj.enrichedEpcs = (data.enrichedEpcs ?? []).map((x: any) => EnrichedEpcs.parse(x)).filter(Boolean);
        obj.enrichedInputEpcs = (data.enrichedInputEpcs ?? []).map((x: any) => EnrichedEpcs.parse(x)).filter(Boolean);
        obj.enrichedInputQuantities = (data.enrichedInputQuantities ?? []).map((x: any) => EnrichedInputOutputEpcs.parse(x)).filter(Boolean);
        obj.enrichedQuantities = (data.enrichedQuantities ?? []).map((x: any) => EnrichedInputOutputEpcs.parse(x)).filter(Boolean);
        obj.enrichedParentId = data.enrichedParentId ? EnrichedEpcs.parse(data.enrichedParentId) : null;
        obj.event = EpcisEvent.parse(data.event);
        obj.extension = data.extension ?? [];
        obj.listExtensions = data.listExtensions ?? [];

        for (const element of obj.listExtensions) {
            if (element?.columnTitle?.text === 'Compliance') {
                const firstItem = element.components?.[0];
                const badgeItem = firstItem?.components?.[0];
                if (badgeItem?.icon) {
                    let status = 'pending';
                    if (badgeItem.icon.title === 'Compliant') {
                        status = 'complete';
                    } else if (badgeItem.icon.title === 'Not compliant') {
                        status = 'invalid';
                    } else if (badgeItem.icon.title === 'Failed') {
                        status = 'failed';
                    }
                    obj.compliance = {
                        title: badgeItem.icon.title,
                        badgeTitle: badgeItem.icon.badge,
                        badgeColor: badgeItem.icon.badgeColor,
                        badgeColorHex: ConvertRGBAToHex(badgeItem.icon.badgeColor.r, badgeItem.icon.badgeColor.g, badgeItem.icon.badgeColor.b, badgeItem.icon.badgeColor.a),
                        badgeTextColor: badgeItem.icon.badgeTextColor,
                        badgeTextColorHex: ConvertRGBAToHex(badgeItem.icon.badgeTextColor.r, badgeItem.icon.badgeTextColor.g, badgeItem.icon.badgeTextColor.b, badgeItem.icon.badgeTextColor.a),
                        color: badgeItem.icon.color,
                        colorHex: ConvertRGBAToHex(badgeItem.icon.color.r, badgeItem.icon.color.g, badgeItem.icon.color.b, badgeItem.icon.color.a),
                        status: status
                    };
                }
                break;
            }
        }

        return obj;
    }

}

export interface Compliance {
    title: string;
    badgeTitle: string;
    badgeColor: any;
    badgeColorHex: string;
    badgeTextColor: any;
    badgeTextColorHex: string;
    color: any;
    colorHex: string;
    status: string;
}

/**
 * One line of the compliance result, e.g. a single rule/check outcome.
 */
export interface ComplianceMessage {
    title: string;
    message: string;
    status: string;
}

/**
 * One compliance result, i.e. one repository's verdict on the event.
 */
export interface ComplianceEntry {
    repository: string;
    status: string;
    statusReason: string;
    messages: ComplianceMessage[];
    rawJson: any;
}

/**
 * Compliance status of an event, from SAGA's compliance-module via
 * `epcis-events/compliance/`.
 *
 * Upstream returns a list: an event can be checked against several compliance
 * repositories and each one reports its own status and reason. The envelope
 * around that list is not contracted, so the payload is normalised into
 * `entries` rather than destructured, and `rawJson` is kept so the UI can always
 * fall back to the untouched response.
 */
export class ComplianceDetail extends RawJsonDict {
    entries: ComplianceEntry[] = [];

    private static readonly MESSAGE_KEYS = ['message', 'description', 'detail', 'details'];
    private static readonly TITLE_KEYS = ['title', 'name', 'ruleName', 'checkName', 'label', 'type'];
    private static readonly STATUS_KEYS = ['status', 'state', 'result', 'outcome', 'compliant'];
    private static readonly REASON_KEYS = ['statusReason', 'reason', 'statusMessage', 'statusDescription'];
    private static readonly REPOSITORY_KEYS = ['complianceRepository', 'repository', 'repositoryName', 'complianceRepositoryName'];

    /** Keys where the list of results may sit when the response is an envelope. */
    private static readonly LIST_KEYS = ['data', 'statuses', 'results', 'content', 'items'];

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): ComplianceDetail | null {
        if (!data) {
            return null;
        }

        const obj = new ComplianceDetail(data);
        obj.entries = ComplianceDetail.toList(data).map(item => ComplianceDetail.parseEntry(item));

        return obj;
    }

    /**
     * Normalises the response into the list of results, whether it arrived as a
     * bare array, an envelope wrapping one, or a single object.
     */
    private static toList(data: any): any[] {
        if (Array.isArray(data)) {
            return data;
        }

        if (data && typeof data === 'object') {
            for (const key of ComplianceDetail.LIST_KEYS) {
                if (Array.isArray(data[key])) {
                    return data[key];
                }
            }
        }

        return [data];
    }

    private static parseEntry(item: any): ComplianceEntry {
        const statusReason = ComplianceDetail.readFirst(item, ComplianceDetail.REASON_KEYS);

        const messages: ComplianceMessage[] = [];
        ComplianceDetail.collect(item, messages, 0);

        return {
            repository: ComplianceDetail.readFirst(item, ComplianceDetail.REPOSITORY_KEYS),
            status: ComplianceDetail.readFirst(item, ComplianceDetail.STATUS_KEYS),
            statusReason: statusReason,
            // The reason is shown on its own row, never repeat it as a message
            messages: messages.filter(x => x.message !== statusReason),
            rawJson: item,
        };
    }

    /**
     * Depth limited so a deeply nested or self referencing payload cannot hang the UI.
     */
    private static collect(node: any, out: ComplianceMessage[], depth: number): void {
        if (!node || depth > 6 || out.length >= 50) {
            return;
        }

        if (Array.isArray(node)) {
            for (const item of node) {
                ComplianceDetail.collect(item, out, depth + 1);
            }
            return;
        }

        if (typeof node !== 'object') {
            return;
        }

        const message = ComplianceDetail.readFirst(node, ComplianceDetail.MESSAGE_KEYS);
        if (message) {
            out.push({
                title: ComplianceDetail.readFirst(node, ComplianceDetail.TITLE_KEYS),
                message: message,
                status: ComplianceDetail.readFirst(node, ComplianceDetail.STATUS_KEYS),
            });
        }

        for (const key of Object.keys(node)) {
            const value = node[key];
            if (value && typeof value === 'object') {
                ComplianceDetail.collect(value, out, depth + 1);
            }
        }
    }

    /**
     * First key holding a non empty scalar. Objects are skipped, a nested
     * `message: {...}` is left for `collect` to walk into.
     */
    private static readFirst(node: any, keys: string[]): string {
        if (!node || typeof node !== 'object') {
            return '';
        }

        for (const key of keys) {
            const value = node[key];
            if (value === null || value === undefined || typeof value === 'object') {
                continue;
            }
            const text = String(value).trim();
            if (text) {
                return text;
            }
        }

        return '';
    }

}

export class Vocabulary extends RawJsonDict {
    id: string;
    displayName: string;
    recordTime: string;
    recordDate: Date;
    attributes: { id: string; value: string }[] = [];
    children: any[] = [];

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): Vocabulary | null {
        if (!data) {
            return null;
        }

        const obj = new Vocabulary(data);
        obj.id = data.id;
        obj.displayName = data.displayName;
        obj.recordTime = data.recordTime;
        obj.recordDate = new Date(data.recordTime);
        obj.attributes = data.attributes ?? [];
        obj.children = data.children ?? [];

        return obj;
    }

    getInitial(): string {
        const names = this.displayName.split(' ');
        return names.map(name => name[0] ?? '').join('');
    }
}

export class MasterData extends RawJsonDict {
    id: number;
    uuid: string;
    name: string;
    source: string;
    type: string;
    key: string;
    traceId: string;
    meta: any;
    attribute: any;
    ownerType: string;
    app?: TheApp | null;
    project?: Project | null;
    site?: Company | null;
    company?: Company | null;
    product?: Product | null;
    tags: Tag[] = [];
    coverPhoto?: MediaObject | null;
    media: MediaObject[] = [];
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): MasterData | null {
        if (!data) {
            return null;
        }

        const obj = new MasterData(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.name = data.name;
        obj.source = data.source;
        obj.type = data.type;
        obj.key = data.key;
        obj.traceId = data.trace_id;
        obj.ownerType = data.owner_type;
        obj.meta = data.meta;
        obj.attribute = data.attribute;

        if (!obj.name || obj.name.length === 0) {
            obj.name = obj.attribute?.['name'] ?? '';
        }

        if (data.app) {
            obj.app = TheApp.parse(data.app);
        }

        if (data.project) {
            obj.project = Project.parse(data.project);
        }

        if (data.site) {
            obj.site = Company.parse(data.site);
        }

        if (data.company) {
            obj.company = Company.parse(data.company);
        }

        if (data.product) {
            obj.product = Product.parse(data.product);
        }

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.cover_photo) {
            obj.coverPhoto = MediaObject.parse(data.cover_photo);
        }

        if (data.media) {
            obj.media = data.media.map((x: any) => MediaObject.parse(x)).filter(Boolean);
        }

        if (data.tags) {
            obj.tags = data.tags.map((x: any) => Tag.parse(x)).filter(Boolean);
        }

        return obj;
    }

}

//Codes
export class CodeMasterData extends RawJsonDict {
    id: number;
    source: string;
    uid: string;
    name: string;
    type: string;
    ownerType: string;
    attribute: any;
    meta: any;
    app?: TheApp | null;
    project?: Project | null;
    site?: Company | null;
    parent?: CodeMasterData | null;
    created?: Date;
    lastModified?: Date;
    status?: string;
    statusResult?: any;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): CodeMasterData | null {
        if (!data) {
            return null;
        }

        const obj = new CodeMasterData(data);
        obj.id = data.id;
        obj.uid = data.uid;
        obj.source = data.source;
        obj.name = data.name;
        obj.type = data.type;
        obj.ownerType = data.ownerType;
        obj.meta = data.meta;
        obj.attribute = data.attribute;
        obj.status = data.status;
        obj.statusResult = data.status_result;

        if (data.app) {
            obj.app = TheApp.parse(data.app);
        }

        if (data.project) {
            obj.project = Project.parse(data.project);
        }

        if (data.site) {
            obj.site = Company.parse(data.site);
        }

        if (data.parent) {
            obj.parent = CodeMasterData.parse(data.parent);
        }

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return obj;
    }
}

export class CodeOrder extends RawJsonDict {
    id: number;
    uuid: string;
    source: string;
    localOrderId: string;
    issuerOrderId: string;
    status: string;
    productUId: string;
    quantity: number;
    site?: Site | null;
    issuer?: CodeMasterData | null;
    format?: CodeMasterData | null;
    rawResponse: any;
    meta: any;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): CodeOrder | null {
        if (!data) {
            return null;
        }

        const obj = new CodeOrder(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.source = data.source;
        obj.localOrderId = data.local_order_uid;
        obj.issuerOrderId = data.issuer_order_uid;
        obj.status = data.status;
        obj.productUId = data.product_uid;
        obj.quantity = data.quantity;
        obj.rawResponse = data.raw_response;
        obj.meta = data.meta ?? {};

        if (data.site) {
            obj.site = Site.parse(data.site);
        }

        if (data.issuer) {
            obj.issuer = CodeMasterData.parse(data.issuer);
        }

        if (data.format) {
            obj.format = CodeMasterData.parse(data.format);
        }

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return obj;
    }

}

export class Code extends RawJsonDict {
    id: number;
    uuid: string;
    batchId: string;
    traceCode: string;
    productUuid: string;
    companyUuid: string;
    queryCode: string;
    scratchCode: string;
    activated: boolean;
    validated: boolean;
    claimed: boolean;
    manufacturerData: any;
    meta: any;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): Code | null {
        if (!data) {
            return null;
        }

        const obj = new Code(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.batchId = data.batch_id;
        obj.traceCode = data.trace_code;
        obj.productUuid = data.product_uuid;
        obj.companyUuid = data.company_uuid;
        obj.queryCode = data.query_code;
        obj.scratchCode = data.scratch_code;
        obj.activated = data.activated ?? false;
        obj.validated = data.validated ?? false;
        obj.claimed = data.claimed ?? false;
        obj.manufacturerData = data.manufacturer_data;
        obj.meta = data.meta ?? {};

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return obj;
    }
}

//GData - Generic Data Storage
export class GData extends RawJsonDict {
    id: number;
    uuid: string;
    uid: string;
    key: string;
    value: any;
    name: string;
    description: string;
    type: string;
    order: string;
    level: string;
    active: boolean;
    meta: any;
    attribute: any;
    ownerType: string;
    app?: TheApp | null;
    project?: Project | null;
    site?: Company | null;
    parent?: GData | null;
    user?: User | null;
    coverPhoto?: MediaObject | null;
    media: MediaObject[] = [];
    tags: Tag[] = [];
    created?: Date;
    lastModified?: Date;
    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): GData | null {
        if (!data) {
            return null;
        }

        const obj = new GData(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.uid = data.uid;
        obj.key = data.key;
        obj.value = data.value;
        obj.name = data.name;
        obj.description = data.description;
        obj.type = data.type;
        obj.ownerType = data.owner_type;
        obj.meta = data.meta ?? {};
        obj.attribute = data.attribute;

        if (!obj.name || obj.name.length === 0) {
            obj.name = obj.attribute?.['name'] ?? '';
        }

        if (data.app) {
            obj.app = TheApp.parse(data.app);
        }

        if (data.project) {
            obj.project = Project.parse(data.project);
        }

        if (data.site) {
            obj.site = Company.parse(data.site);
        }

        if (data.parent) {
            obj.parent = GData.parse(data.parent);
        }

        if (data.user) {
            obj.user = User.parse(data.user);
        }

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.cover_photo) {
            obj.coverPhoto = MediaObject.parse(data.cover_photo);
        }

        if (data.media) {
            obj.media = data.media.map((x: any) => MediaObject.parse(x)).filter(Boolean);
        }

        if (data.tags) {
            obj.tags = data.tags.map((x: any) => Tag.parse(x)).filter(Boolean);
        }

        return obj;
    }

    /**
     * Get value as string
     */
    getValueAsString(): string {
        if (typeof this.value === 'string') {
            return this.value;
        }
        return JSON.stringify(this.value);
    }

    /**
     * Get value as boolean
     */
    getValueAsBoolean(): boolean {
        if (typeof this.value === 'boolean') {
            return this.value;
        }
        if (typeof this.value === 'string') {
            return this.value.toLowerCase() === 'true';
        }
        return !!this.value;
    }

    /**
     * Get value as JSON object
     */
    getValueAsJSON(): any {
        if (typeof this.value === 'object') {
            return this.value;
        }
        if (typeof this.value === 'string') {
            try {
                return JSON.parse(this.value);
            } catch (error) {
                return null;
            }
        }
        return null;
    }
}

/**
 * Types for activity log actions and ownership levels
 */
export type ActivityLogOwnerType = 'app' | 'project' | 'company' | 'site';
export type ActivityLogAction = 'add' | 'edit' | 'delete';

/**
 * The main ActivityLog class
 */
export class ActivityLog extends RawJsonDict {
    id: number;
    uuid: string;
    user: User;

    // Ownership Context
    app: TheApp;
    project?: Project | null;
    company?: Company | null;
    site?: Company | null;
    ownerType: ActivityLogOwnerType;

    // Target Info
    targetType: string; // e.g. 'vocabulary'
    targetId: string;   // the trace_id
    action: ActivityLogAction;

    // Data
    snapshot: any;       // The state before the action
    remark: string;      // The automated summary (e.g. "Updated: name")

    // Timestamps
    created: Date;
    lastModified: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): ActivityLog | null {
        if (!data) {
            return null;
        }

        const obj = new ActivityLog(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.user = User.parse(data.user);
        obj.app = TheApp.parse(data.app);
        obj.project = Project.parse(data.project);
        obj.company = Company.parse(data.company);
        obj.site = Company.parse(data.site);
        obj.ownerType = data.owner_type || 'app';
        obj.targetType = data.target_type;
        obj.targetId = data.target_id;
        obj.action = data.action;
        obj.snapshot = data.snapshot || {};
        obj.remark = data.remark;

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return obj;
    }

    getDisplayName() {
        if (this.targetType == 'vocabulary') {
            return this.snapshot?.name;
        }

        return '';
    }
}

export class CompanyPointWallet extends RawJsonDict {
    id: number;
    uuid: string;
    company?: Company | null;
    totalRemain: string;
    totalAdd: string;
    totalUse: string;
    type: string;
    meta: any;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): CompanyPointWallet | null {
        if (!data) { return null; }

        const obj = new CompanyPointWallet(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.totalRemain = data.total_remain;
        obj.totalAdd = data.total_add;
        obj.totalUse = data.total_use;
        obj.type = data.type;
        obj.meta = data.meta ?? {};

        if (data.company) {
            obj.company = Company.parse(data.company);
        }

        if (data.created) {
            try { obj.created = new Date(data.created); } catch (_) {}
        }
        if (data.last_modified) {
            try { obj.lastModified = new Date(data.last_modified); } catch (_) {}
        }

        return obj;
    }
}

export class BlockchainSetting extends RawJsonDict {
    id: number;
    uuid: string;
    company?: Company | null;
    enabled: boolean;
    rules: any[];
    meta: any;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): BlockchainSetting | null {
        if (!data) { return null; }

        const obj = new BlockchainSetting(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.enabled = data.enabled ?? false;
        obj.rules = data.rules ?? [];
        obj.meta = data.meta ?? {};

        if (data.company) {
            obj.company = Company.parse(data.company);
        }

        if (data.created) {
            try { obj.created = new Date(data.created); } catch (_) {}
        }
        if (data.last_modified) {
            try { obj.lastModified = new Date(data.last_modified); } catch (_) {}
        }

        return obj;
    }
}

// ---- EPCIS trace lineage: shapes follow core-api-itrace docs/epcis-trace-lineage-api.md ----

// Which events count as "included". '' or 'any' = wildcard; OR across rules, AND
// within a rule; no rules = every event included.
export interface TraceRule {
    event_type?: string;
    action?: string;
    biz_step?: string;
}

// Which rules a trace response applied
export interface TraceFilter {
    setting: string | null;                          // dashed UUID (settings API uses hex: strip dashes to compare)
    scope: 'company' | 'app' | 'preview' | null;     // preview = the request's own rules; null = nothing applies
    company: { id: number; title: string | null } | null;  // may be the root company, not the scanning site
}

export type TraceLinkType = 'contains' | 'consumed';  // packed inside the node / an input it was made from

// Whether events naming a node are missing from the trace: still waiting for SAGA to
// confirm them (pending), or rejected by SAGA (failed). The trace only holds synced events.
export type TraceSyncStatus = 'ok' | 'pending' | 'failed';

export interface TraceSync {
    status: TraceSyncStatus;
    pending: number;
    failed: number;
    events: Array<{                                  // newest few unsynced events
        id: number;
        uid: string;                                 // SAGA document id
        event_type: string;
        status: 'pending' | 'fail';
        created: string;
        message: string | null;                      // last sync message, e.g. SAGA's failure reason
    }>;
}

// ---- local endpoints: trace-children / trace-history ----

export interface TraceLocalRequest {
    site_id: number;
    epc: string;
    rules?: TraceRule[];
    include_filtered?: boolean;                      // trace-history only
    include_details?: boolean;                       // add readable names (item, biz step, location…)
    direction?: TraceDirection;                      // trace-children: default 'down'
}

// down: what an item holds and was made of; up: what it was packed into and used to make
export type TraceDirection = 'down' | 'up';

// include_details: what an identifier is, from local master data
export interface TraceItem {
    name: string | null;                             // null when no master data matches
    product_id: string | null;                       // the identifier minus its serial / lot
    kind: string | null;                             // sgtin, sscc, lgtin…
}

export interface TraceVocabRef {
    id: string;
    name: string | null;
}

// The doc's TracePage is the standard paging block, which Paging already parses
export type TracePage = Paging;

export interface TraceEventSummary {
    id: number;
    uid: string;                                     // SAGA document id
    saga_event_id: string;                           // for advanced-query sagaEventIds
    event_type: string;
    action: 'ADD' | 'OBSERVE' | 'DELETE' | string;
    biz_step: string;
    event_time: string | null;
    parent: string | null;
    epc_count: number;
    site: { id: number; title: string } | null;
    included: boolean;
    matched_rule: number | null;                     // index into the response's rules; null when not included
    // include_details only
    biz_step_name?: string | null;
    biz_location?: TraceVocabRef | null;
    read_point?: TraceVocabRef | null;
    disposition?: TraceVocabRef | null;
}

export interface TraceChild {
    identifier: string;
    type: TraceLinkType;
    event: TraceEventSummary;
    child_count: number;                             // 0 = leaf: no expand arrow
    sync_status: TraceSyncStatus;                    // unsynced events naming this child
    item?: TraceItem;                                // include_details only
}

export interface TraceHistoryItem extends TraceEventSummary {
    roles: Array<'parent' | 'child' | 'output' | 'input'>;
}

export interface TraceLocalResponse<T> {
    page: TracePage;
    data: T[];
    node: string;
    filter: TraceFilter;
    rules: TraceRule[];
    sync: TraceSync;                                 // for the node itself
    node_item?: TraceItem;                           // include_details only
}

export type TraceChildrenResponse = TraceLocalResponse<TraceChild>;
export type TraceHistoryResponse = TraceLocalResponse<TraceHistoryItem>;

// trace-start: from a scanned epc, or an event (sagaEventId, or documentId for one not synced yet)
export interface TraceStartRequest {
    site_id: number;
    epc?: string;
    sagaEventId?: string;
    documentId?: string;
    rules?: TraceRule[];
    include_details?: boolean;
    direction?: TraceDirection;                      // decides seeds[].child_count
}

export interface TraceStartResponse {
    start: {
        by: 'epc' | 'event';
        event: TraceEventSummary | null;             // when started from a synced event
        event_status: 'complete' | 'pending' | 'fail' | 'not_found' | null;
        message: string | null;                      // why there is nothing to trace, when there isn't
    };
    seeds: Array<{ identifier: string; child_count: number; sync_status: TraceSyncStatus; item?: TraceItem }>;
    sync: TraceSync;                                 // for the seeds
    filter: TraceFilter;
    rules: TraceRule[];
}

// ---- SAGA endpoint: trace-lineage ----

export interface TraceLineageRequest {
    site_id: number;
    epc?: string;
    sagaEventId?: string;
    partition?: number;
    rules?: TraceRule[];
    debug?: boolean;
}

export interface TraceContainsLink {
    type: 'contains';
    event: string;
    action: 'ADD' | 'DELETE' | string;               // ADD = packed, DELETE = unpacked
    parent: string;
    children: string[];
    included: boolean;                               // false = the event was filtered out of `events`
}

export interface TraceConsumedLink {
    type: 'consumed';
    event: string;
    outputs: string[];
    inputs: string[];
    included: boolean;
}

export type TraceLink = TraceContainsLink | TraceConsumedLink;

export interface TraceLineage {
    seeds: string[];
    events: Epcis[];                                 // parsed SAGA event wrappers
    links: TraceLink[];
    filter: TraceFilter;
    stats: {
        events_traced: number;
        events_included: number;
        identifiers: number;
        levels: number;
        requests: number;
    };
    // Only when the request sent debug: true
    debug?: {
        rules: TraceRule[];
        excluded: Epcis[];
        matched_rules: Record<string, number>;       // sagaEventId -> index into rules
    };
}

export interface TraceLineageResponse {
    data: TraceLineage;
}

// ---- settings: event-trace-settings ----

// The setting that actually filters a company's traces right now: its own when
// enabled, otherwise the root company's, otherwise the app's, otherwise none.
export interface TraceEffectiveSetting {
    scope: 'company' | 'app' | null;
    setting: string | null;                          // 32-char hex, no dashes
    company: { id: number; title: string } | null;
    rules: TraceRule[];
}

export interface EventTraceSettingPatch {
    enabled?: boolean;
    rules?: TraceRule[];
}

export class EventTraceSetting extends RawJsonDict {
    id: number;
    uuid: string;                                    // 32-char hex, no dashes
    app: number | null;
    company?: Company | null;
    enabled: boolean;
    rules: TraceRule[];
    meta: Record<string, unknown>;
    effective?: TraceEffectiveSetting;               // get-by-company and PATCH only
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): EventTraceSetting | null {
        if (!data) { return null; }

        const obj = new EventTraceSetting(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.app = data.app ?? null;
        obj.enabled = data.enabled ?? false;
        obj.rules = data.rules ?? [];
        obj.meta = data.meta ?? {};
        obj.effective = data.effective;

        // An app-level row comes back with company: {}
        if (data.company?.id) {
            obj.company = Company.parse(data.company);
        }

        if (data.created) {
            try { obj.created = new Date(data.created); } catch (_) {}
        }
        if (data.last_modified) {
            try { obj.lastModified = new Date(data.last_modified); } catch (_) {}
        }

        return obj;
    }
}

export type ComplianceEventType = 'ObjectEvent' | 'AggregationEvent' | 'TransformationEvent';

// Ordered filter rule; the first rule matching an event wins and its check
// flags are injected into the EPCIS XML. The cert types are NOT injected — they
// are read by the certificate validate endpoints when SAGA calls back with the
// event type.
export interface ComplianceRule {
    event_type: ComplianceEventType | 'any' | '';  // 'any' or '' = wildcard
    action: string;                                // 'any' or '' = wildcard
    biz_step: string;                              // 'any' or '' = wildcard
    check_company_cert: boolean;
    company_cert_type: string;                     // '' = any cert type satisfies the check
    // Three states, mirroring check_company_cert but with an optional narrowing:
    //     false                     off
    //     true                      on, EPC lists unspecified -> check any list
    //     ['parentEPC','childEPC']  on, narrowed to these EPC lists
    // The names reach SAGA comma-joined in <saga:checkProductCert> (true emits the tag
    // as 'true'), and are opaque to us: any string is valid, so a custom name works
    // exactly like a standard one. PRODUCT_CERT_TOKENS (compliance-settings.component.ts)
    // only holds the standard names the picker suggests. `string` is the legacy
    // single-token shape, still returned by rows saved before this became a list —
    // normalizeProductCert() there is the one place that expands it (including 'both').
    check_product_cert: string[] | string | boolean;
    product_cert_type: string;                     // '' = any cert type satisfies the check
}

export class ComplianceSetting extends RawJsonDict {
    id: number;
    uuid: string;
    company?: Company | null;
    // Set when this row is the company's override within one project. Absent on the
    // company-wide row, which is how a sub-company site opts out and is also the
    // legacy shape for root companies.
    project?: { id: number; uuid: string; title: string } | null;
    enabled: boolean;
    // The two checks a rule carries are overridden independently. false = that side
    // follows the project's rules; true with an empty `rules` means "nothing to check"
    // on that side, NOT a fallback. Both read the same `rules` list below — the toggle
    // decides which SIDE of those rules is in effect.
    overrideCompanyRules: boolean;
    overrideProductRules: boolean;
    rules: ComplianceRule[];
    meta: any;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): ComplianceSetting | null {
        if (!data) { return null; }

        const obj = new ComplianceSetting(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.enabled = data.enabled ?? false;
        obj.overrideCompanyRules = data.override_company_rules ?? false;
        obj.overrideProductRules = data.override_product_rules ?? false;
        obj.rules = data.rules ?? [];
        obj.meta = data.meta ?? {};

        if (data.company) {
            obj.company = Company.parse(data.company);
        }

        // Serialized as {} rather than null when absent (the company-wide row).
        if (data.project && data.project.id) {
            obj.project = data.project;
        }

        if (data.created) {
            try { obj.created = new Date(data.created); } catch (_) {}
        }
        if (data.last_modified) {
            try { obj.lastModified = new Date(data.last_modified); } catch (_) {}
        }

        return obj;
    }
}

// The project-level standard rule set every company and site beneath it follows,
// plus the master on/off switch. No override flag — the project IS the standard.
export class ProjectComplianceSetting extends RawJsonDict {
    id: number;
    uuid: string;
    project?: { id: number; uuid: string; title: string } | null;
    enabled: boolean;
    rules: ComplianceRule[];
    meta: any;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): ProjectComplianceSetting | null {
        if (!data) { return null; }

        const obj = new ProjectComplianceSetting(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.enabled = data.enabled ?? false;
        obj.rules = data.rules ?? [];
        obj.meta = data.meta ?? {};

        if (data.project && data.project.id) {
            obj.project = data.project;
        }

        if (data.created) {
            try { obj.created = new Date(data.created); } catch (_) {}
        }
        if (data.last_modified) {
            try { obj.lastModified = new Date(data.last_modified); } catch (_) {}
        }

        return obj;
    }
}

export class BlockchainBulkStatus extends RawJsonDict {
    companyId: number;
    enabled: boolean;
    walletRemain: string;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): BlockchainBulkStatus | null {
        if (!data) { return null; }

        const obj = new BlockchainBulkStatus(data);
        obj.companyId = data.company_id;
        obj.enabled = data.enabled ?? false;
        obj.walletRemain = data.wallet_remain ?? '0.0000';

        return obj;
    }
}

export class CompanyPointWalletTransaction extends RawJsonDict {
    id: number;
    uuid: string;
    wallet: any;
    amount: string;
    description: string;
    meta: any;
    created?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): CompanyPointWalletTransaction | null {
        if (!data) { return null; }

        const obj = new CompanyPointWalletTransaction(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.wallet = data.wallet;
        obj.amount = data.amount;
        obj.description = data.description;
        obj.meta = data.meta ?? {};

        if (data.created) {
            try { obj.created = new Date(data.created); } catch (_) {}
        }

        return obj;
    }
}

export class WebhookEndpoint extends RawJsonDict {
    id: number;
    uuid: string;
    name: string;
    type: string;
    url: string;
    active: boolean;
    authType: string;
    authHeaderName: string;
    authHeaderValue: string;
    extraHeaders: any;
    app: number | null;
    company: number | null;
    project: number | null;
    meta: any;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): WebhookEndpoint | null {
        if (!data) { return null; }
        const obj = new WebhookEndpoint(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.name = data.name;
        obj.type = data.type;
        obj.url = data.url;
        obj.active = data.active ?? true;
        obj.authType = data.auth_type ?? 'none';
        obj.authHeaderName = data.auth_header_name ?? '';
        obj.authHeaderValue = data.auth_header_value ?? '';
        obj.extraHeaders = data.extra_headers ?? {};
        obj.app = data.app ?? null;
        obj.company = data.company ?? null;
        obj.project = data.project ?? null;
        obj.meta = data.meta ?? {};
        if (data.created) {
            try { obj.created = new Date(data.created); } catch (_) {}
        }
        if (data.last_modified) {
            try { obj.lastModified = new Date(data.last_modified); } catch (_) {}
        }
        return obj;
    }
}

export class WebhookHistoryItem extends RawJsonDict {
    id: number;
    endpoint: any;
    payload: any;
    statusCode: number | null;
    success: boolean;
    error: string | null;
    response: any;
    created?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): WebhookHistoryItem | null {
        if (!data) { return null; }
        const obj = new WebhookHistoryItem(data);
        obj.id = data.id;
        obj.endpoint = data.endpoint ?? null;
        obj.payload = data.payload ?? {};
        obj.statusCode = data.status_code ?? null;
        obj.success = data.success ?? false;
        obj.error = data.error ?? null;
        obj.response = data.response ?? {};
        if (data.created) {
            try { obj.created = new Date(data.created); } catch (_) {}
        }
        return obj;
    }
}

export class CertificateDocument {
    id: number;
    uuid: string;
    title: string;
    file: string;
    mimeType: string;
    fileSize: number;

    static parse(data: any): CertificateDocument | null {
        if (!data) { return null; }
        const obj = new CertificateDocument();
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.title = data.title;
        obj.file = data.file;
        obj.mimeType = data.mime_type;
        obj.fileSize = data.file_size;
        return obj;
    }
}

/**
 * The outcome of the last verification check, as recorded by the backend in
 * `meta.verification`.
 *
 * `verified` is tri-state: true confirmed good, false confirmed not good, and
 * null could not be determined — an issuing authority being unreachable is not
 * the same as a certificate having failed.
 */
export interface CertificateVerification {
    verified: boolean | null;
    verifiedAt: Date | null;
    method: string;
    provider: string | null;
    message: string;
}

export class Certificate extends RawJsonDict {
    id: number;
    uuid: string;
    title: string;
    certNumber: string;
    type: string;
    issuedBy: string;
    issuedDate: Date | null;
    expiryDate: Date | null;
    status: 'active' | 'expired' | 'revoked' | 'pending';
    description: string;
    ownerType: string;
    ownerId: number;
    app: any;
    company: any;
    project: any;
    documents: CertificateDocument[] = [];
    attribute: Record<string, any>;
    meta: Record<string, any>;
    verification: CertificateVerification | null = null;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): Certificate | null {
        if (!data) { return null; }

        const obj = new Certificate(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.title = data.title;
        obj.certNumber = data.cert_number;
        obj.type = data.type;
        obj.issuedBy = data.issued_by;
        obj.status = data.status;
        obj.description = data.description;
        obj.ownerType = data.owner_type;
        obj.ownerId = data.owner_id;
        obj.app = data.app ?? null;
        obj.company = data.company ?? null;
        obj.project = data.project ?? null;
        obj.attribute = data.attribute ?? {};
        obj.meta = data.meta ?? {};

        // epoch seconds → Date
        obj.issuedDate = data.issued_date ? new Date(data.issued_date * 1000) : null;
        obj.expiryDate = data.expiry_date ? new Date(data.expiry_date * 1000) : null;

        obj.documents = Array.isArray(data.documents)
            ? data.documents.map((d: any) => CertificateDocument.parse(d)).filter(Boolean)
            : [];

        // Surfaced off meta so templates don't reach into the raw system blob.
        const verification = data.meta?.verification;
        obj.verification = verification ? {
            // Absent means inconclusive, not failed — don't coerce to false.
            verified: verification.verified ?? null,
            verifiedAt: verification.verified_at ? new Date(verification.verified_at * 1000) : null,
            method: verification.method ?? '',
            provider: verification.provider ?? null,
            message: verification.message ?? '',
        } : null;

        if (data.created) {
            try { obj.created = new Date(data.created); } catch (_) {}
        }
        if (data.last_modified) {
            try { obj.lastModified = new Date(data.last_modified); } catch (_) {}
        }

        return obj;
    }
}
// *********** EPCIS Issues (SAGA failed events) **************

export type EpcisIssueLevel = 'app' | 'project' | 'company' | 'site';

export class EpcisIssue extends RawJsonDict {
    id: string;
    createdAt: Date | null;
    modifiedAt: Date | null;
    topicName: string;
    failedReason: string;
    details: string;
    className: string;
    documentId: string;
    eventId: string[];
    additionalData: any;
    partition: number;

    // Detail endpoint only, already parsed by the API (never a JSON string)
    listOfEvents?: any;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): EpcisIssue | null {
        if (!data) {
            return null;
        }

        const obj = new EpcisIssue(data);
        obj.id = data.id;
        obj.topicName = data.topicName;
        obj.failedReason = data.failedReason;
        obj.details = data.details;
        obj.className = data.className;
        obj.documentId = data.documentId;
        obj.eventId = Array.isArray(data.eventId) ? data.eventId : [];
        obj.additionalData = data.additionalData || {};
        obj.partition = data.partition;
        obj.listOfEvents = data.listOfEvents;

        obj.createdAt = null;
        if (data.createdAt) {
            try { obj.createdAt = new Date(data.createdAt); } catch (_) {}
        }

        obj.modifiedAt = null;
        if (data.modifiedAt) {
            try { obj.modifiedAt = new Date(data.modifiedAt); } catch (_) {}
        }

        return obj;
    }

    getEventType(): string {
        try {
            return this.rawJson.eventType || '';
        } catch (error) {
            return '';
        }
    }

    /**
     * `details` is often a raw JSON blob from SAGA, e.g.
     * {"timestamp":"...","error":"Bad Request","exception":"com.fracturecode...",
     *  "message":"Unknown unit of measure ...","path":"http://10.0.0.1:5001/api/..."}
     * Parsed lazily so the list can show the human message instead of the envelope.
     */
    private _parsedDetails: any | undefined;

    getParsedDetails(): any | null {
        if (this._parsedDetails !== undefined) {
            return this._parsedDetails;
        }

        this._parsedDetails = null;

        const raw = (this.details || '').trim();
        if (raw.startsWith('{') || raw.startsWith('[')) {
            try {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === 'object') {
                    this._parsedDetails = parsed;
                }
            } catch (error) {
                // Not JSON, keep the raw text
            }
        }

        return this._parsedDetails;
    }

    /**
     * The readable part of `details`. Falls back to the raw text when it is not a
     * JSON envelope. Note `path` / `exception` are deliberately not surfaced here,
     * they carry SAGA's internal hostnames and class paths.
     */
    getDetailsMessage(): string {
        const parsed = this.getParsedDetails();

        if (parsed) {
            const message = parsed.message || parsed.error || parsed.detail || '';
            return String(message).replace(/\s+/g, ' ').trim();
        }

        const details = (this.details || '').replace(/\s+/g, ' ').trim();
        if (details) {
            return details;
        }

        // No details at all - the envelope may have landed in failedReason instead
        const fallback = this.getParsedFailedReason();
        if (fallback) {
            const message = fallback.message || fallback.error || '';
            if (message) {
                return String(message).replace(/\s+/g, ' ').trim();
            }
        }

        return '';
    }

    /**
     * `failedReason` sometimes carries a JSON envelope instead of an enum value.
     * Parsed lazily, same as `details`.
     */
    private _parsedFailedReason: any | undefined;

    getParsedFailedReason(): any | null {
        if (this._parsedFailedReason !== undefined) {
            return this._parsedFailedReason;
        }

        this._parsedFailedReason = null;

        const raw = (this.failedReason || '').trim();
        if (raw.startsWith('{') || raw.startsWith('[')) {
            try {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === 'object') {
                    this._parsedFailedReason = parsed;
                }
            } catch (error) {
                // Not JSON, keep the raw text
            }
        }

        return this._parsedFailedReason;
    }

    /**
     * `failedReason` is not guaranteed to be a valid enum value - raw error strings
     * and JSON envelopes have been observed in its place. Normalises it to something
     * short enough to sit on one line as a label.
     */
    getFailedReasonLabel(): string {
        const raw = (this.failedReason || '').trim();

        if (!raw) {
            return 'UNKNOWN';
        }

        // A well formed enum value, e.g. GENERAL_ERROR
        if (/^[A-Z0-9_]+$/.test(raw)) {
            return raw;
        }

        // A JSON envelope in the reason slot, use its error / exception instead
        const parsed = this.getParsedFailedReason();
        if (parsed) {
            const label = parsed.error || parsed.exception || '';
            if (label) {
                const parts = String(label).split('.');
                return parts[parts.length - 1];
            }
            return 'ERROR';
        }

        const flat = raw.replace(/\s+/g, ' ');
        return flat.length > 40 ? `${flat.substring(0, 40).trim()}...` : flat;
    }

    // *********** Raw error payload **************

    /**
     * The parsed error envelope, wherever it landed - `details` or `failedReason`.
     */
    getErrorEnvelope(): any | null {
        return this.getParsedDetails() || this.getParsedFailedReason();
    }

    /**
     * True when there is any raw error payload worth inspecting.
     */
    hasErrorPayload(): boolean {
        return !!(this.getErrorEnvelope() || (this.details || '').trim() || this.hasRawFailedReason());
    }

    /**
     * True when the record is malformed enough that the raw payload should be shown
     * up front rather than hidden behind a toggle: a JSON envelope in the reason
     * slot, or nothing readable to display as a message.
     */
    needsRawErrorView(): boolean {
        return this.hasRawFailedReason() || !this.getDetailsMessage();
    }

    /**
     * The envelope flattened into ordered key/value rows for display. Known keys
     * come first, anything unexpected follows so nothing is silently dropped.
     */
    getErrorFields(): { key: string, value: string }[] {
        const envelope = this.getErrorEnvelope();
        if (!envelope) {
            return [];
        }

        const knownOrder = ['error', 'exception', 'message', 'timestamp', 'status', 'path'];
        const keys = Object.keys(envelope);
        const ordered = [
            ...knownOrder.filter(k => keys.includes(k)),
            ...keys.filter(k => !knownOrder.includes(k)),
        ];

        return ordered.map((key) => {
            const value = envelope[key];
            let text: string;

            if (value === null || value === undefined) {
                text = '-';
            } else if (typeof value === 'object') {
                try {
                    text = JSON.stringify(value);
                } catch (error) {
                    text = String(value);
                }
            } else {
                text = String(value);
            }

            return { key, value: text };
        });
    }

    /**
     * The full raw payload, pretty printed when it is JSON.
     */
    getRawErrorText(): string {
        const envelope = this.getErrorEnvelope();

        if (envelope) {
            try {
                return JSON.stringify(envelope, null, 2);
            } catch (error) {
                // Fall through to the raw strings
            }
        }

        return this.details || this.failedReason || '';
    }

    /**
     * True when `failedReason` was not a clean enum value, so the UI can point at
     * the detail dialog for the real content.
     */
    hasRawFailedReason(): boolean {
        const raw = (this.failedReason || '').trim();
        return !!raw && !/^[A-Z0-9_]+$/.test(raw);
    }

    /**
     * Short error label from the envelope, e.g. 'Bad Request'.
     */
    getDetailsError(): string {
        const parsed = this.getParsedDetails();
        return parsed?.error ? String(parsed.error) : '';
    }

    /**
     * The exception class name only, without its package.
     */
    getDetailsException(): string {
        const parsed = this.getParsedDetails();
        if (!parsed?.exception) {
            return '';
        }
        const parts = String(parsed.exception).split('.');
        return parts[parts.length - 1];
    }

    getShortClassName(): string {
        if (!this.className) {
            return '';
        }
        const parts = this.className.split('.');
        return parts[parts.length - 1];
    }

    getRetryCount(): number {
        try {
            return this.listOfEvents?.retryCount ?? 0;
        } catch (error) {
            return 0;
        }
    }
}

/**
 * Filter metadata from `issue-criteria`, drives the dynamic filter UI.
 */
export class EpcisIssueCriteria extends RawJsonDict {
    fieldName: string;
    type: string; // 'text' | 'select' | 'vocabulary'
    label: string;
    groupName: string;
    uri: string;
    allowUnlisted: boolean;
    options: any[];

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): EpcisIssueCriteria | null {
        if (!data) {
            return null;
        }

        const obj = new EpcisIssueCriteria(data);
        obj.fieldName = data.fieldName;
        obj.type = data.type;
        obj.label = data.label?.text || data.fieldName;
        obj.groupName = data.groupName?.text || '';
        obj.uri = data.uri;
        obj.allowUnlisted = data.allowUnlisted === true;
        obj.options = Array.isArray(data.options) ? data.options : [];

        return obj;
    }
}

/**
 * One day of counters from `issue-counters`.
 * Note: counters are app-wide regardless of level, and the reason keys are not
 * guaranteed to be valid failedReason values.
 */
export class EpcisIssueCounter extends RawJsonDict {
    date: string;
    reasons: { reason: string, total: number, active: number, resolved: number }[];

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): EpcisIssueCounter | null {
        if (!data) {
            return null;
        }

        const obj = new EpcisIssueCounter(data);
        obj.date = data.date;
        obj.reasons = [];

        const counters = data.data || {};
        for (const reason of Object.keys(counters)) {
            const item = counters[reason] || {};
            obj.reasons.push({
                reason,
                total: item.total ?? 0,
                active: item.active ?? 0,
                resolved: item.resolved ?? 0,
            });
        }

        return obj;
    }

    getTotal(): number {
        return this.reasons.reduce((sum, x) => sum + x.total, 0);
    }
}
